import { APIClient } from "../helpers/api_helper";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../types/api";

const api = new APIClient();

const RESOURCE = "/users";

export const USER_ROLE_SLUGS = [
  "owner",
  "manager",
  "receptionist",
  "sdr",
  "professional",
] as const;

export type UserRoleSlug = (typeof USER_ROLE_SLUGS)[number];

export interface UserUnitRef {
  id: number;
  name: string;
}

export interface UserRoleRef {
  id: number;
  name: string;
}

export interface UserProfessionalRef {
  id: number;
  name: string;
  enabled_services?: Array<{ id: number; name: string }>;
  schedules?: Array<{
    day_of_week: number;
    start_time: string;
    end_time: string;
    break_label?: string | null;
  }>;
}

export type MembershipAccessStatus = "none" | "pending" | "active";

export interface User {
  id: number;
  tenant_id: number;
  name: string;
  email: string;
  locale?: string | null;
  roles?: string[] | UserRoleRef[];
  units?: UserUnitRef[];
  professional?: UserProfessionalRef | null;
  /** Studio login access: none | pending invite | active membership. */
  access_status?: MembershipAccessStatus;
  /** ISO-8601 UTC timestamp of last invite (when access_status is pending). */
  invited_at?: string | null;
  /** Conta Adatis: false when account was created via OTP without password. */
  has_password?: boolean;
  /** Conta Adatis linked to Google OAuth. */
  google_linked?: boolean;
  google_email?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateUserPayload {
  name: string;
  email: string;
  password: string;
  locale?: string | null;
  roles: string[];
  units: number[];
  professional_id?: number | null;
}

export interface UpdateUserPayload {
  name?: string;
  email?: string;
  password?: string;
  locale?: string | null;
  roles?: string[];
  units?: number[];
  professional_id?: number | null;
}

export interface UserListParams extends PaginationParams {
  search?: string;
}

export const getUsers = (
  params?: UserListParams
): Promise<PaginatedApiResponse<User>> =>
  api.get<PaginatedApiResponse<User>>(RESOURCE, params);

export const getUser = (id: number): Promise<ApiResponse<User>> =>
  api.get<ApiResponse<User>>(`${RESOURCE}/${id}`);

export const createUser = (
  payload: CreateUserPayload
): Promise<ApiResponse<User>> =>
  api.create<ApiResponse<User>>(RESOURCE, payload);

export interface InviteUserPayload {
  email: string;
  name: string;
  role: UserRoleSlug;
  units: number[];
  professional_id?: number | null;
}

export interface InviteUserResult {
  user: User;
  membership: {
    id: number;
    status: string;
    invited_at: string | null;
  };
  setup_required: boolean;
  invite_url?: string | null;
}

export const inviteUser = (
  payload: InviteUserPayload
): Promise<ApiResponse<InviteUserResult>> =>
  api.create<ApiResponse<InviteUserResult>>(`${RESOURCE}/invite`, payload);

export const updateUser = (
  id: number,
  payload: UpdateUserPayload
): Promise<ApiResponse<User>> =>
  api.update<ApiResponse<User>>(`${RESOURCE}/${id}`, payload);

export const deleteUser = (
  id: number
): Promise<ApiResponse<Record<string, never>>> =>
  api.delete<ApiResponse<Record<string, never>>>(`${RESOURCE}/${id}`);

export const extractUserRoleNames = (user: User): string[] => {
  if (!user.roles?.length) {
    return [];
  }

  const first = user.roles[0];

  if (typeof first === "string") {
    return user.roles as string[];
  }

  return (user.roles as UserRoleRef[]).map((role) => role.name);
};

export const extractUserUnitIds = (user: User): number[] => {
  if (!user.units?.length) {
    return [];
  }

  return user.units.map((unit) => unit.id);
};
